import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ShieldCheck, AlertTriangle, XCircle, HelpCircle, RefreshCw, 
  Terminal, Server, Camera, ExternalLink, CheckCircle2, Clock, 
  Cpu, HardDrive, Network, Monitor, Layers, Eye
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  'Docker & Host Memory': Cpu,
  'Cloudflare Tunnel & Network': Network,
  'Disk Storage & Volume': HardDrive,
  'QEMU, KVM & Graphics': Monitor,
  'Windows OS & Software': Layers
};

export default function WinResilienceMonitor() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [screenshotUrl, setScreenshotUrl] = useState('/screenshots/win_openbalancer_live.webp');
  const [screenshotTime, setScreenshotTime] = useState(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [autoRefreshSec, setAutoRefreshSec] = useState(5);

  const fetchStatus = async (silent = false) => {
    try {
      if (!silent) setRefreshing(true);
      const res = await fetch('/api/win-resilience/status');
      const json = await res.json();
      if (json.success && json.data) {
        setData(json.data);
      }
    } catch (err) {
      if (!silent) toast.error('Failed to fetch resilience status: ' + err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const fetchScreenshot = async () => {
    try {
      setCapturingScreenshot(true);
      const res = await fetch('/api/win-resilience/screenshot');
      const json = await res.json();
      if (json.success) {
        setScreenshotUrl(json.url + '?t=' + Date.now());
        setScreenshotTime(json.timestamp);
        toast.success('Captured live screenshot of win.openbalancer.com');
      }
    } catch (err) {
      toast.error('Screenshot capture failed: ' + err.message);
    } finally {
      setCapturingScreenshot(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchScreenshot();

    // 5-second auto refresh interval
    const interval = setInterval(() => {
      fetchStatus(true);
      setAutoRefreshSec(5);
    }, 5000);

    const timerInt = setInterval(() => {
      setAutoRefreshSec((prev) => (prev > 1 ? prev - 1 : 5));
    }, 1000);

    return () => {
      clearInterval(interval);
      clearInterval(timerInt);
    };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-dark-300">
        <RefreshCw className="w-8 h-8 animate-spin mb-3 text-purple-400" />
        <p className="text-sm font-medium">Running 50 real-time probes against win.openbalancer.com...</p>
      </div>
    );
  }

  const summary = data?.summary || { total: 50, ok: 0, warn: 0, fail: 0, unknown: 0, scorePercent: 0 };
  const checks = data?.checks || [];

  const categories = ['ALL', 'Docker & Host Memory', 'Cloudflare Tunnel & Network', 'Disk Storage & Volume', 'QEMU, KVM & Graphics', 'Windows OS & Software'];

  const filteredChecks = selectedCategory === 'ALL' 
    ? checks 
    : checks.filter(c => c.category === selectedCategory);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'OK':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            <CheckCircle2 className="w-3.5 h-3.5" /> OK
          </span>
        );
      case 'WARN':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/20">
            <AlertTriangle className="w-3.5 h-3.5" /> WARN
          </span>
        );
      case 'FAIL':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-rose-500/10 text-rose-400 border border-rose-500/20">
            <XCircle className="w-3.5 h-3.5" /> FAIL
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-500/10 text-slate-400 border border-slate-500/20">
            <HelpCircle className="w-3.5 h-3.5" /> UNKNOWN
          </span>
        );
    }
  };

  return (
    <div className="space-y-6">
      {/* Top Header Card */}
      <div className="bg-dark-800/80 border border-dark-700/60 backdrop-blur-xl rounded-2xl p-6 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  50-Point Resilience & Fault-Tolerance Matrix
                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 font-mono">
                    win.openbalancer.com
                  </span>
                </h2>
                <p className="text-xs text-dark-300 mt-0.5 flex items-center gap-3">
                  <span>Host: macmini-primary (100.83.83.8)</span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3 text-purple-400" /> Refreshing in {autoRefreshSec}s
                  </span>
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => fetchStatus(false)}
              disabled={refreshing}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/30 text-xs font-semibold transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Run 50 Probes
            </button>
            <a
              href="https://win.openbalancer.com"
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-dark-700/80 hover:bg-dark-700 text-dark-200 text-xs font-semibold transition-all border border-dark-600"
            >
              Open VM GUI <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* Resilience Metrics Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-6 pt-6 border-t border-dark-700/50">
          <div className="bg-dark-900/50 border border-dark-700/40 rounded-xl p-3.5">
            <div className="text-xs text-dark-400">Score</div>
            <div className="text-2xl font-bold text-purple-400 mt-1">{summary.scorePercent}%</div>
            <div className="text-[10px] text-dark-400 mt-0.5">{summary.ok} of 50 Passed</div>
          </div>
          <div className="bg-dark-900/50 border border-emerald-500/20 rounded-xl p-3.5">
            <div className="text-xs text-emerald-400">OK</div>
            <div className="text-2xl font-bold text-emerald-400 mt-1">{summary.ok}</div>
            <div className="text-[10px] text-dark-400 mt-0.5">Operational</div>
          </div>
          <div className="bg-dark-900/50 border border-amber-500/20 rounded-xl p-3.5">
            <div className="text-xs text-amber-400">WARN</div>
            <div className="text-2xl font-bold text-amber-400 mt-1">{summary.warn}</div>
            <div className="text-[10px] text-dark-400 mt-0.5">Non-critical</div>
          </div>
          <div className="bg-dark-900/50 border border-rose-500/20 rounded-xl p-3.5">
            <div className="text-xs text-rose-400">FAIL</div>
            <div className="text-2xl font-bold text-rose-400 mt-1">{summary.fail}</div>
            <div className="text-[10px] text-dark-400 mt-0.5">Blockers</div>
          </div>
          <div className="bg-dark-900/50 border border-slate-500/20 rounded-xl p-3.5">
            <div className="text-xs text-slate-400">UNKNOWN</div>
            <div className="text-2xl font-bold text-slate-300 mt-1">{summary.unknown}</div>
            <div className="text-[10px] text-dark-400 mt-0.5">No SSH / Unreachable</div>
          </div>
        </div>
      </div>

      {/* Main Grid: Left Category List, Right Screenshot & Live View */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Category Filters & Checks List (2 cols) */}
        <div className="lg:col-span-2 space-y-4">
          
          {/* Category Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto pb-2 scrollbar-none">
            {categories.map((cat) => {
              const Icon = CATEGORY_ICONS[cat] || ShieldCheck;
              const isActive = selectedCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all border ${
                    isActive 
                      ? 'bg-purple-600/20 border-purple-500/40 text-purple-300 shadow-sm' 
                      : 'bg-dark-800/60 border-dark-700/50 text-dark-300 hover:text-white hover:bg-dark-700/50'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {cat === 'ALL' ? 'All 50 Checks' : cat}
                </button>
              );
            })}
          </div>

          {/* Checks List */}
          <div className="space-y-2.5">
            {filteredChecks.map((check) => (
              <motion.div
                key={check.id}
                layout
                onClick={() => setSelectedCheck(check)}
                className="group bg-dark-800/60 hover:bg-dark-700/60 border border-dark-700/50 hover:border-purple-500/30 rounded-xl p-4 cursor-pointer transition-all flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-3.5">
                  <div className="w-8 h-8 rounded-lg bg-dark-900/60 border border-dark-700 flex items-center justify-center font-mono text-xs font-bold text-dark-300 group-hover:text-purple-400 group-hover:border-purple-500/40 transition-all">
                    #{check.id}
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white group-hover:text-purple-300 transition-all">
                      {check.title}
                    </div>
                    <div className="text-xs text-dark-400 line-clamp-1 mt-0.5">
                      {check.desc}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right hidden sm:block">
                    <div className="text-[11px] font-mono text-dark-300 truncate max-w-[200px]">
                      {check.output}
                    </div>
                  </div>
                  {getStatusBadge(check.status)}
                  <Eye className="w-4 h-4 text-dark-400 group-hover:text-purple-400 transition-all" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Right Pane: Live Proof Screenshot */}
        <div className="space-y-4">
          <div className="bg-dark-800/80 border border-dark-700/60 backdrop-blur-xl rounded-2xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-purple-400" />
                <h3 className="text-sm font-bold text-white">Live Screenshot Proof</h3>
              </div>
              <button
                onClick={fetchScreenshot}
                disabled={capturingScreenshot}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/30 text-xs font-medium transition-all disabled:opacity-50"
              >
                <RefreshCw className={`w-3 h-3 ${capturingScreenshot ? 'animate-spin' : ''}`} />
                Capture Now
              </button>
            </div>

            <div className="relative rounded-xl overflow-hidden border border-dark-700 bg-dark-900 aspect-video flex items-center justify-center group">
              <iframe
                src="http://100.83.83.8:8006/vnc.html?autoconnect=true&resize=scale"
                className="w-full h-full object-cover border-0"
                title="QEMU Windows 11 VNC"
                sandbox="allow-scripts allow-same-origin allow-forms"
              />
              <div className="absolute inset-0 bg-dark-950/40 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center pointer-events-none">
                <a
                  href="http://100.83.83.8:8006/vnc.html"
                  target="_blank"
                  rel="noreferrer"
                  className="px-3.5 py-2 rounded-xl bg-purple-600 text-white text-xs font-semibold shadow-lg flex items-center gap-1.5 pointer-events-auto"
                >
                  <ExternalLink className="w-3.5 h-3.5" /> Open Full VNC Client
                </a>
              </div>
            </div>

            <div className="text-[11px] text-dark-400 flex items-center justify-between font-mono bg-dark-900/60 p-2.5 rounded-xl border border-dark-700/40">
              <span>Target: win.openbalancer.com</span>
              <span>{screenshotTime ? new Date(screenshotTime).toLocaleTimeString() : 'Live'}</span>
            </div>
          </div>
        </div>

      </div>

      {/* Drill-Down Inspection Modal */}
      <AnimatePresence>
        {selectedCheck && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-950/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-dark-800 border border-dark-700 rounded-2xl max-w-2xl w-full p-6 space-y-5 shadow-2xl relative"
            >
              <div className="flex items-start justify-between gap-4 border-b border-dark-700 pb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-purple-400 font-bold px-2 py-0.5 bg-purple-500/10 rounded-md border border-purple-500/20">
                      Check #{selectedCheck.id}
                    </span>
                    <span className="text-xs text-dark-400 font-medium">
                      {selectedCheck.category}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-white mt-1">
                    {selectedCheck.title}
                  </h3>
                </div>
                {getStatusBadge(selectedCheck.status)}
              </div>

              <div className="space-y-3">
                <div>
                  <div className="text-xs text-dark-400 font-medium">Description</div>
                  <div className="text-sm text-dark-200 mt-1">{selectedCheck.desc}</div>
                </div>

                <div>
                  <div className="text-xs text-dark-400 font-medium flex items-center gap-1.5">
                    <Terminal className="w-3.5 h-3.5 text-purple-400" /> Executed Command / Probe
                  </div>
                  <div className="mt-1 font-mono text-xs text-purple-300 bg-dark-950 p-3 rounded-xl border border-dark-700/60 overflow-x-auto">
                    {selectedCheck.command}
                  </div>
                </div>

                <div>
                  <div className="text-xs text-dark-400 font-medium flex items-center gap-1.5">
                    <Server className="w-3.5 h-3.5 text-emerald-400" /> Probe Response / Output
                  </div>
                  <div className="mt-1 font-mono text-xs text-dark-200 bg-dark-950 p-3 rounded-xl border border-dark-700/60 whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {selectedCheck.output || 'No output recorded'}
                  </div>
                </div>

                <div className="text-right text-[11px] text-dark-400 font-mono pt-2">
                  Last verified: {new Date(selectedCheck.lastChecked).toLocaleString()}
                </div>
              </div>

              <div className="pt-2 border-t border-dark-700 flex justify-end">
                <button
                  onClick={() => setSelectedCheck(null)}
                  className="px-4 py-2 rounded-xl bg-dark-700 hover:bg-dark-600 text-white text-xs font-semibold transition-all"
                >
                  Close Inspection
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
